import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    AlertTriangle,
    Stethoscope,
    Check,
    X,
    Send,
    Clock,
    User as UserIcon,
    RotateCcw,
    ChevronDown,
    ChevronUp,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { describeSymptom } from "@/constants/symptoms";
import { TID } from "@/constants/testIds";

const STATUS_TABS = [
    { key: "new", label: "New", icon: AlertTriangle, cls: "text-veda-error" },
    { key: "claimed", label: "On it", icon: Stethoscope, cls: "text-veda-brand" },
    { key: "resolved", label: "Resolved", icon: Check, cls: "text-veda-onSurface" },
    { key: "dismissed", label: "Dismissed", icon: X, cls: "text-veda-muted" },
];

function StatusPill({ status }) {
    const s = status || "new";
    const map = {
        new: { label: "New", cls: "bg-veda-error text-white" },
        claimed: { label: "On it", cls: "bg-veda-brand text-white" },
        resolved: {
            label: "Resolved",
            cls: "border border-veda-border bg-veda-surface2 text-veda-onSurface",
        },
        dismissed: {
            label: "Dismissed",
            cls: "border border-veda-border bg-veda-surface2 text-veda-muted",
        },
    };
    const v = map[s] || map.new;
    return (
        <span className={`rounded-pill px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${v.cls}`}>
            {v.label}
        </span>
    );
}

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const secs = Math.floor((Date.now() - d.getTime()) / 1000);
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return d.toLocaleString();
}

function TriageCard({ item, myDoctorId, onChanged }) {
    const [expanded, setExpanded] = useState(false);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const triage = item.triage || {};
    const status = triage.status || "new";
    const mine = triage.claimed_by && triage.claimed_by === myDoctorId;
    const claimedByOther = triage.claimed_by && !mine;

    async function patch(action, extraNote) {
        setBusy(true);
        try {
            const body = { action };
            const n = (extraNote ?? note).trim();
            if (n) body.note = n;
            await api.patch(`/doctor/triage/${item.submission_id}`, body);
            setNote("");
            toast.success(
                {
                    claim: "Claimed. Reach out to the patient.",
                    unclaim: "Released back to New.",
                    resolve: "Marked resolved.",
                    dismiss: "Dismissed.",
                    reopen: "Reopened.",
                    note: "Note added.",
                }[action] || "Updated.",
            );
            onChanged?.();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not update.");
        } finally {
            setBusy(false);
        }
    }

    const c = item.computed || {};
    const topSymptoms = (c.top_symptoms || []).slice(0, 6);

    return (
        <li
            data-testid={TID.triageItem(item.submission_id)}
            className={`border p-4 md:p-5 ${
                status === "new"
                    ? "border-veda-error/50 bg-veda-error/[0.03]"
                    : "border-veda-border bg-veda-surface"
            }`}
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                        <StatusPill status={status} />
                        <p className="font-display truncate text-lg text-veda-onSurface">
                            {item.user?.name || "Anonymous patient"}
                        </p>
                    </div>
                    <p className="mt-1 text-xs text-veda-muted">
                        {item.user?.email ? item.user.email + " · " : ""}
                        <Clock size={10} className="mr-0.5 inline" />
                        {timeAgo(item.created_at)}
                        {triage.claimed_by && (
                            <>
                                {" · "}
                                <UserIcon size={10} className="mr-0.5 inline" />
                                {mine
                                    ? "you"
                                    : triage.claimed_by_name || triage.claimed_by}
                            </>
                        )}
                    </p>
                    {c.top_concern && (
                        <p className="mt-2 text-sm text-veda-onSurface">
                            <span className="font-semibold">Concern:</span>{" "}
                            {c.top_concern}
                            {c.severity && ` · ${c.severity}`}
                            {c.duration && ` · ${c.duration}`}
                        </p>
                    )}
                    {c.prakriti?.primary && (
                        <p className="mt-1 text-xs italic text-veda-muted">
                            Prakriti leans{" "}
                            <span className="capitalize">{c.prakriti.primary}</span>
                        </p>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {status === "new" && (
                        <button
                            type="button"
                            onClick={() => patch("claim")}
                            disabled={busy}
                            data-testid={TID.triageClaim(item.submission_id)}
                            className="inline-flex items-center gap-1 border border-veda-brand bg-veda-brand px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white disabled:opacity-60"
                        >
                            <Stethoscope size={11} /> Claim
                        </button>
                    )}
                    {status === "claimed" && mine && (
                        <>
                            <button
                                type="button"
                                onClick={() => patch("resolve")}
                                disabled={busy}
                                data-testid={TID.triageResolve(item.submission_id)}
                                className="inline-flex items-center gap-1 border border-veda-brand bg-veda-brand px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white disabled:opacity-60"
                            >
                                <Check size={11} /> Resolve
                            </button>
                            <button
                                type="button"
                                onClick={() => patch("unclaim")}
                                disabled={busy}
                                data-testid={TID.triageUnclaim(item.submission_id)}
                                className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface disabled:opacity-60"
                            >
                                Release
                            </button>
                        </>
                    )}
                    {(status === "new" || status === "claimed") && (mine || !claimedByOther) && (
                        <button
                            type="button"
                            onClick={() => patch("dismiss")}
                            disabled={busy}
                            data-testid={TID.triageDismiss(item.submission_id)}
                            className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted hover:border-veda-error hover:text-veda-error disabled:opacity-60"
                        >
                            <X size={11} /> Dismiss
                        </button>
                    )}
                    {(status === "resolved" || status === "dismissed") && (
                        <button
                            type="button"
                            onClick={() => patch("reopen")}
                            disabled={busy}
                            data-testid={TID.triageReopen(item.submission_id)}
                            className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface disabled:opacity-60"
                        >
                            <RotateCcw size={11} /> Reopen
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setExpanded((e) => !e)}
                        className="grid h-7 w-7 place-items-center text-veda-muted hover:text-veda-onSurface"
                        aria-label="Toggle details"
                    >
                        {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                </div>
            </div>

            {expanded && (
                <div className="mt-4 border-t border-veda-divider pt-4">
                    {topSymptoms.length > 0 && (
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Symptoms flagged
                            </p>
                            <ul className="mt-2 space-y-2">
                                {topSymptoms.map((s) => {
                                    const info = describeSymptom(s.symptom_id);
                                    if (!info) return null;
                                    return (
                                        <li
                                            key={s.symptom_id}
                                            className="border border-veda-divider bg-veda-surface2/50 p-3"
                                        >
                                            <div className="flex items-baseline justify-between gap-2">
                                                <p className="font-semibold text-veda-onSurface">
                                                    {info.name}
                                                </p>
                                                <span className="text-[10px] uppercase tracking-[0.12em] text-veda-muted">
                                                    intensity {s.score}
                                                </span>
                                            </div>
                                            {info.short && (
                                                <p className="mt-1 text-xs text-veda-onSurface">
                                                    {info.short}
                                                </p>
                                            )}
                                            {info.dosha_view && (
                                                <p className="mt-1 text-xs italic text-veda-muted">
                                                    {info.dosha_view}
                                                </p>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    {(triage.notes || []).length > 0 && (
                        <div className="mt-4">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Timeline
                            </p>
                            <ul className="mt-2 space-y-2 border-l border-veda-border pl-3 text-xs">
                                {triage.notes.map((n, i) => (
                                    <li key={i}>
                                        <p className="text-veda-onSurface">
                                            <span className="font-semibold">
                                                {n.by_name || n.by_role}
                                            </span>{" "}
                                            <span className="text-veda-muted">
                                                · {timeAgo(n.at)}
                                            </span>
                                        </p>
                                        <p className="mt-0.5 italic text-veda-muted">
                                            {n.text}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="mt-4 flex gap-2">
                        <input
                            type="text"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Add an internal note (visible to admins & Vaidyas)"
                            data-testid={TID.triageNoteInput(item.submission_id)}
                            className="flex-1 border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                            maxLength={1000}
                        />
                        <button
                            type="button"
                            onClick={() => note.trim() && patch("note", note)}
                            disabled={busy || !note.trim()}
                            data-testid={TID.triageNoteSubmit(item.submission_id)}
                            className="inline-flex items-center gap-1 bg-veda-inverse px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onInverse disabled:opacity-60"
                        >
                            <Send size={11} /> Note
                        </button>
                    </div>
                </div>
            )}
        </li>
    );
}

export default function VaidyaTriage() {
    const { user } = useAuth();
    const [status, setStatus] = useState("new");
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({ new: 0, claimed: 0, resolved: 0, dismissed: 0 });
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // load current tab + counts (fire all in parallel)
            const [cur, all] = await Promise.all([
                api.get("/doctor/triage", { params: { status, limit: 100 } }),
                api.get("/doctor/triage", { params: { limit: 500 } }),
            ]);
            setItems(cur.data || []);
            const c = { new: 0, claimed: 0, resolved: 0, dismissed: 0 };
            for (const it of all.data || []) {
                const s = it.triage?.status || "new";
                if (c[s] !== undefined) c[s]++;
            }
            setCounts(c);
        } catch {
            toast.error("Could not load triage.");
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => {
        load();
    }, [load]);

    const myDoctorId = user?.doctor_id;
    const total = useMemo(() => counts.new + counts.claimed, [counts]);

    return (
        <div data-testid={TID.triagePage} className="pb-16">
            <div className="border-b border-veda-error/40 bg-veda-error/[0.03]">
                <div className="veda-container py-6 md:py-10">
                    <Link
                        to={user?.role === "admin" ? "/admin/overview" : "/doctor/dashboard"}
                        className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                    >
                        <ArrowLeft size={13} /> {user?.role === "admin" ? "Admin overview" : "Doctor dashboard"}
                    </Link>
                    <div className="mt-4 flex flex-wrap items-baseline justify-between gap-3">
                        <div>
                            <p className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-error">
                                <AlertTriangle size={12} /> Urgent triage
                            </p>
                            <h1 className="font-display mt-1 text-3xl text-veda-onSurface md:text-5xl">
                                Red-flag inbox.
                            </h1>
                            <p className="mt-2 max-w-xl text-sm text-veda-muted">
                                Patients whose quiz flagged severe symptoms.
                                Claim one, reach out, and mark it resolved.
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                Live queue
                            </p>
                            <p className="font-display text-3xl text-veda-error">
                                {total}
                            </p>
                            <p className="text-[10px] text-veda-muted">
                                new + on it
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="veda-container py-6 md:py-8">
                <div className="flex flex-wrap gap-2">
                    {STATUS_TABS.map((t) => {
                        const active = status === t.key;
                        const Icon = t.icon;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setStatus(t.key)}
                                data-testid={TID.triageFilterStatus(t.key)}
                                className={`inline-flex items-center gap-2 border px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                                    active
                                        ? "border-veda-inverse bg-veda-inverse text-veda-onInverse"
                                        : "border-veda-border bg-veda-surface text-veda-onSurface"
                                }`}
                            >
                                <Icon size={12} className={active ? "" : t.cls} />
                                {t.label}
                                <span
                                    className={`rounded-pill px-1.5 text-[10px] font-bold ${
                                        active
                                            ? "bg-white/20 text-white"
                                            : "bg-veda-surface2 text-veda-muted"
                                    }`}
                                >
                                    {counts[t.key] || 0}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <ul className="mt-6 space-y-3">
                    {loading && (
                        <li className="text-sm text-veda-muted">Loading…</li>
                    )}
                    {!loading && items.length === 0 && (
                        <li className="border border-dashed border-veda-border bg-veda-surface2/40 p-8 text-center text-sm text-veda-muted">
                            Nothing in this queue.
                        </li>
                    )}
                    {items.map((it) => (
                        <TriageCard
                            key={it.submission_id}
                            item={it}
                            myDoctorId={myDoctorId}
                            onChanged={load}
                        />
                    ))}
                </ul>
            </div>
        </div>
    );
}
